import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Sparkles, Store, Loader2, ArrowLeft } from 'lucide-react';
import { getImageUrl } from '@/utils/imageUrl';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function EgeriesListPage() {
  const [egeries, setEgeries] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    axios.get(`${API}/marketplace/egeries`)
      .then(r => setEgeries(r.data || []))
      .catch(() => setEgeries([]))
      .finally(() => setLoading(false));
  }, []);

  return (
    <div className="min-h-screen bg-gradient-to-br from-pink-50 via-white to-purple-50">
      {/* Header */}
      <header className="border-b bg-white/80 backdrop-blur sticky top-0 z-10">
        <div className="max-w-6xl mx-auto px-4 py-3 flex items-center gap-3">
          <Link to="/">
            <Button variant="ghost" size="sm" className="gap-1" data-testid="egeries-back-home">
              <ArrowLeft className="h-4 w-4" /> Accueil
            </Button>
          </Link>
          <div className="flex-1" />
          <div className="flex items-center gap-2 text-pink-600 font-bold">
            <Sparkles className="h-5 w-5" />
            <span className="hidden sm:inline">Nos Égéries</span>
          </div>
        </div>
      </header>

      {/* Hero */}
      <section className="max-w-6xl mx-auto px-4 pt-10 pb-6 text-center">
        <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-pink-100 text-pink-700 text-xs font-semibold mb-4">
          <Sparkles className="h-3 w-3" />
          Programme Ambassadeurs · ServisPro Makiti
        </div>
        <h1 className="text-4xl sm:text-5xl lg:text-6xl font-black tracking-tight text-slate-900 mb-3">
          Nos Égéries
        </h1>
        <p className="text-base text-slate-600 max-w-2xl mx-auto">
          Ces personnalités guinéennes nous accompagnent en soutenant les boutiques du marketplace.
          Achetez chez elles et faites confiance à leur recommandation.
        </p>
      </section>

      {/* List */}
      <section className="max-w-6xl mx-auto px-4 pb-16">
        {loading ? (
          <div className="flex items-center justify-center py-24 text-slate-400">
            <Loader2 className="h-6 w-6 animate-spin mr-2" /> Chargement…
          </div>
        ) : egeries.length === 0 ? (
          <div className="text-center py-24 text-slate-400" data-testid="egeries-empty">
            <Sparkles className="h-14 w-14 mx-auto mb-3 opacity-40" />
            <p className="text-sm">Nos égéries seront bientôt annoncées.</p>
          </div>
        ) : (
          <div className="grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
            {egeries.map(e => (
              <Card
                key={e.id}
                className="overflow-hidden bg-white hover:shadow-xl transition-all border-pink-100"
                data-testid={`egerie-card-${e.id}`}
              >
                {/* Photo */}
                <div className="aspect-[4/3] bg-gradient-to-br from-pink-100 to-purple-100 relative">
                  {e.photo ? (
                    <img
                      src={e.photo}
                      alt={e.full_name}
                      className="w-full h-full object-cover"
                      loading="lazy"
                      decoding="async"
                    />
                  ) : (
                    <div className="w-full h-full flex items-center justify-center text-pink-400">
                      <Sparkles className="h-14 w-14" />
                    </div>
                  )}
                  <div className="absolute top-3 left-3 bg-white/95 backdrop-blur px-2.5 py-1 rounded-full text-[10px] font-bold text-pink-700 flex items-center gap-1">
                    <Sparkles className="h-3 w-3" /> Égérie officielle
                  </div>
                </div>

                {/* Info */}
                <div className="p-4">
                  <h3 className="font-bold text-lg text-slate-900 mb-1">{e.full_name}</h3>
                  {e.bio && (
                    <p className="text-sm text-slate-600 mb-3 line-clamp-3">{e.bio}</p>
                  )}

                  {/* Sponsored shops */}
                  <div className="border-t pt-3">
                    <p className="text-[10px] uppercase tracking-wide text-slate-400 font-semibold mb-2">
                      Boutique{e.shops.length > 1 ? 's' : ''} sponsorisée{e.shops.length > 1 ? 's' : ''}
                    </p>
                    <div className="space-y-1.5">
                      {e.shops.map(s => (
                        <Link
                          key={s.id}
                          to={`/makiti/shop/${s.id}`}
                          className="flex items-center gap-2 p-2 rounded-md hover:bg-pink-50 transition-colors group"
                          data-testid={`egerie-shop-link-${s.id}`}
                        >
                          {s.logo ? (
                            <img
                              src={getImageUrl(s.logo, 'thumb')}
                              alt={s.name}
                              className="h-8 w-8 rounded-full object-cover shrink-0"
                              loading="lazy"
                            />
                          ) : (
                            <div className="h-8 w-8 rounded-full bg-slate-100 flex items-center justify-center shrink-0">
                              <Store className="h-4 w-4 text-slate-400" />
                            </div>
                          )}
                          <div className="min-w-0 flex-1">
                            <p className="text-sm font-semibold text-slate-800 truncate group-hover:text-pink-700">
                              {s.name}
                            </p>
                            {s.sector && (
                              <p className="text-[10px] text-slate-500 truncate">{s.sector}</p>
                            )}
                          </div>
                        </Link>
                      ))}
                    </div>
                  </div>
                </div>
              </Card>
            ))}
          </div>
        )}
      </section>
    </div>
  );
}
